import type { PixelEvent } from "../types";
import { requestWithRetry } from "./request";

export interface SenderOptions {
  endpoint: string;
  siteId: string;
}

const MAX_BATCH_BYTES = 64 * 1024;
const pending = new Set<{ endpoint: string; body: string }>();

function serializeBatch(siteId: string, events: PixelEvent[]): string {
  return JSON.stringify({ site_id: siteId, events });
}

function chunkBySize(siteId: string, events: PixelEvent[]): string[] {
  if (events.length === 0) return [];
  const full = serializeBatch(siteId, events);
  if (new Blob([full]).size <= MAX_BATCH_BYTES) return [full];
  if (events.length === 1) return [full];
  const mid = Math.floor(events.length / 2);
  return [...chunkBySize(siteId, events.slice(0, mid)), ...chunkBySize(siteId, events.slice(mid))];
}

export async function send(opts: SenderOptions, events: PixelEvent[], useBeacon: boolean): Promise<boolean> {
  if (events.length === 0) return true;
  const batches = chunkBySize(opts.siteId, events);
  const results = await Promise.all(batches.map((body) => sendOne(opts.endpoint, body, useBeacon)));
  return results.every(Boolean);
}

function beacon(endpoint: string, body: string): boolean {
  if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    try {
      const blob = new Blob([body], { type: "text/plain;charset=UTF-8" });
      if (navigator.sendBeacon(endpoint, blob)) return true;
    } catch {
      // The browser may reject its keepalive quota; use fetch as a fallback.
    }
  }
  return false;
}

// A page can close while a fetch or its retry timer is in flight. Reuse the
// exact payload: the collector deduplicates (site_id, event_id), even if the
// original request succeeded but its acknowledgement was lost.
export function flushPending(): void {
  for (const batch of pending) beacon(batch.endpoint, batch.body);
}

async function sendOne(endpoint: string, body: string, useBeacon: boolean): Promise<boolean> {
  if (useBeacon && beacon(endpoint, body)) return true;
  const batch = { endpoint, body };
  pending.add(batch);
  try {
    const response = await requestWithRetry(endpoint, {
      method: "POST",
      body,
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
      keepalive: true,
      credentials: "omit",
      mode: "cors",
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    pending.delete(batch);
  }
}
